// Проекты координатора с брифинга: карточки по сделкам, чек-листы, вехи, журнал.
// ТОЛЬКО ЧТЕНИЕ.
//
// ГЛАВНОЕ ПРО ЭТИ ДАННЫЕ, БЕЗ ЧЕГО ЭКРАН СОВРЁТ
// Карточка проекта заводится САМА, когда кто-то на брифинге открывает экран проекта по
// сделке. Поэтому «карточек столько-то» и «проектов ведётся столько-то» — разные числа,
// и экран не имеет права показывать первое вместо второго. Отсюда колонка «состояние»
// и фильтр «только заполненные», включённый по умолчанию.
//
// ЧЕГО ЗДЕСЬ НЕ УВИДЯТ НИКОГДА
// Денег проекта (план, факт, ставка человеко-часа, суточные, затраты) и дневника работ
// (бригада поимённо с часами и переработками, поездки, расходы, сводка дня от внешней
// модели с оценкой работы человека). Их не отдаёт ядро, потому что их не переносит синк:
// список запрещённого лежит в sync_pbr.py и роняет прогон, если туда вписать лишнее.
// Экран на это не полагается молча — там, где текст закрыт, он говорит, что закрыт.
//
// Замер 28.07.2026 по источнику, ради него раздел и делается: в KV 32 карточки, из них
// заполнены 9, вех 43, пунктов чек-листа 416 при 21 отметке, записей журнала 475.
// Задач у проектов нет ни одной — поле пустое во всех карточках.
import { useMemo, useState } from 'react'

import {
  ИМЯ_СУЩНОСТИ,
  ОГОВОРКИ_ПРОЕКТОВ,
  РАЗМЕР_СТРАНИЦЫ,
  самаяСтарая,
  useЖурналПроектов,
  useПроекты,
  useСправочникиПроектов,
} from '../api/coordProjects'
import type {
  ПроектСтрока,
  ПроектыИтоги,
  СобытиеСтрока,
  СправочникиПроектов,
} from '../api/coordProjects'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, pluralN } from '../lib/format'
import {
  КЛАСС_СОСТОЯНИЯ,
  ПОРОГ_ЗАСТОЯ_ДНЕЙ,
  СОСТОЯНИЯ,
  долязакрытых,
  поКоду,
  состояние,
} from '../lib/coordProjects'

type Вкладка = 'list' | 'log'

export default function CoordProjects() {
  const [вкладка, setВкладка] = useState<Вкладка>('list')
  const refs = useСправочникиПроектов()

  const св = самаяСтарая(refs.data?.freshness)
  const счёт = refs.data?.counts

  return (
    <>
      <div className="page-head">
        <h1>Проекты координатора</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${(св.age_min ?? 0) < 1 ? 'только что' : `${Math.round(св.age_min ?? 0)} мин назад`}`
              + ` (позже всех — ${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity})`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Отсюда нельзя ни завести проект, ни отметить пункт, ни передвинуть веху.</b>{' '}
        Работают в этом на брифинге, он и остаётся источником правды. Денег проекта и
        дневника работ здесь нет вовсе: ставка человеко-часа, суточные, часы и переработки
        бригады — та же группа данных, что ФОТ, и раздел в неё не входит.
      </div>

      {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
          «.filters > * { width: 100% }» поставило бы кнопки в столбец. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([
          ['list', `Карточки${счёт ? ` · заполнено ${счёт.filled} из ${счёт.projects}` : ''}`],
          ['log', `Журнал${счёт ? ` · ${счёт.events}` : ''}`],
        ] as [Вкладка, string][]).map(([код, подпись]) => (
          <button key={код} onClick={() => setВкладка(код)}
                  aria-pressed={вкладка === код}
                  style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
            {подпись}
          </button>
        ))}
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {вкладка === 'list' && <Карточки refs={refs.data} />}
        {вкладка === 'log' && <Журнал refs={refs.data} />}
        <Оговорки заметки={refs.data?.data_notes} />
      </QueryState>
    </>
  )
}

/** Оговорки из ядра. Тексты и числа в них считает то же ядро, что и данные рядом. */
function Оговорки({ заметки }: { заметки?: Record<string, string> }) {
  if (!заметки) return null
  const важные = ОГОВОРКИ_ПРОЕКТОВ.filter((к) => заметки[к])
  if (!важные.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Что нужно знать об этих данных</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {важные.map((к) => <li key={к}>{заметки[к]}</li>)}
      </ul>
    </div>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0}
              onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

// ═══════════════════════════════ карточки ═══════════════════════════════
//
// По умолчанию показываются ТОЛЬКО заполненные, и это не «спрятать неудобное». Пустая
// карточка не несёт ни одного сведения о работе: она значит ровно то, что кто-то однажды
// открыл экран проекта по этой сделке. Список вперемешку отвечал бы на вопрос «по каким
// сделкам открывали экран», а спрашивают его о другом.

function Карточки({ refs }: { refs?: СправочникиПроектов }) {
  const [координатор, setКоординатор] = useState('')
  const [стадия, setСтадия] = useState('')
  const [толькоЗаполненные, setТолькоЗаполненные] = useState(true)
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    coordinator_bx: координатор,
    stage: стадия,
    filled: толькоЗаполненные ? '1' : '',
    q: поиск,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [координатор, стадия, толькоЗаполненные, поиск, start])

  const запрос = useПроекты(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="coord-projects-list">
          <label htmlFor="coord-projects-coord">
            Координатор
            <select id="coord-projects-coord" value={координатор}
                    onChange={(e) => сСброса(setКоординатор)(e.target.value)}>
              <option value="">все</option>
              {(refs?.coordinators ?? []).map((к) => (
                <option key={String(к.coordinator_bx_id)} value={String(к.coordinator_bx_id ?? '')}>
                  {к.coordinator_name ?? String(к.coordinator_bx_id ?? '')}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="coord-projects-stage">
            Стадия карточки
            <select id="coord-projects-stage" value={стадия}
                    onChange={(e) => сСброса(setСтадия)(e.target.value)}>
              <option value="">все</option>
              {(refs?.stages ?? []).filter((с) => с.stage).map((с) => (
                <option key={String(с.stage)} value={String(с.stage)}>
                  {поКоду(refs?.stage_names, с.stage)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="coord-projects-q">
            Поиск
            <input id="coord-projects-q" type="search" value={поиск}
                   placeholder="сделка, судно, координатор"
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={толькоЗаполненные}
                   onChange={(e) => сСброса(setТолькоЗаполненные)(e.target.checked)} />
            только заполненные
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги
            ? ` · ${pluralN(итоги.filled ?? 0, 'заполнена', 'заполнены', 'заполнено')}`
              + ` · ${pluralN(итоги.coordinators ?? 0, 'координатор', 'координатора', 'координаторов')}`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {итоги && <Плитки итоги={итоги} />}
        <Таблица строки={строки} refs={refs} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function Плитки({ итоги }: { итоги: ПроектыИтоги }) {
  const всего = итоги.projects ?? 0
  const заполнено = итоги.filled ?? 0
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">карточек под отбором</div>
        <div className="t-sum">{всего}</div>
        <div className="t-label">заполнено: {заполнено}</div>
      </div>
      <div className="tile">
        <div className="t-label">отмечено пунктов чек-листа</div>
        <div className="t-sum">{итоги.checklist_done ?? 0}</div>
        <div className="t-label">чек-лист у всех проектов одинаковый</div>
      </div>
      <div className="tile">
        <div className="t-label">вех заведено</div>
        <div className="t-sum">{итоги.milestones ?? 0}</div>
        <div className="t-label">срок и процент ведут руками</div>
      </div>
      <div className="tile">
        <div className="t-label">записей журнала, кроме автоматических</div>
        <div className="t-sum">{итоги.events_own ?? 0}</div>
        <div className="t-label">
          {(итоги.no_deal ?? 0) > 0
            ? `${pluralN(итоги.no_deal ?? 0, 'карточка', 'карточки', 'карточек')} без сделки в зеркале`
            : 'все карточки нашли свою сделку'}
        </div>
      </div>
    </div>
  )
}

function Таблица({ строки, refs }: { строки: ПроектСтрока[]; refs?: СправочникиПроектов }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Проектов под этот отбор нет</div>
        <div className="empty-hint">
          Карточки заводят на брифинге, в зеркало они приезжают ближайшим прогоном.
          Снимите фильтр «только заполненные» — тогда покажутся и карточки, которые
          завелись сами при открытии экрана проекта, но так и остались пустыми.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Сделка</th>
            <th>Судно и компания</th>
            <th>Координатор</th>
            <th>Состояние</th>
            <th className="num">Чек-лист</th>
            <th className="num">Вехи</th>
            <th>Последнее движение</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => <Строка key={r.deal_bitrix_id} r={r} refs={refs} />)}
        </tbody>
      </table>
    </div>
  )
}

function Строка({ r, refs }: { r: ПроектСтрока; refs?: СправочникиПроектов }) {
  const сост = состояние(r)
  const доля = долязакрытых(r.checklist_done, r.checklist_total)
  return (
    <tr>
      <td data-label="Сделка">
        <EntityLink kind="deal" id={r.deal_bitrix_id} title={r.deal_title} />
        {!r.deal_in_mirror && (
          <div className="hint">сделки в зеркале ещё нет — заголовок из карточки</div>
        )}
      </td>
      <td data-label="Судно и компания">
        {r.vessel_contact_id
          ? <EntityLink kind="vessel" id={r.vessel_contact_id} title={r.vessel_name} />
          : <span className="hint">судно не привязано</span>}
        <div className="muted">
          {r.company_id
            ? <EntityLink kind="company" id={r.company_id} title={r.company_title} />
            : <span className="hint">компания не привязана</span>}
        </div>
      </td>
      <td data-label="Координатор">
        {r.coordinator_name ?? <span className="hint">не проставлен</span>}
        <div className="hint">{поКоду(refs?.stage_names, r.stage)}</div>
      </td>
      <td data-label="Состояние">
        <span className={КЛАСС_СОСТОЯНИЯ[сост]}>{СОСТОЯНИЯ[сост]}</span>
        {сост === 'stale' && (
          <div className="hint">
            тишина дольше, чем {pluralN(ПОРОГ_ЗАСТОЯ_ДНЕЙ, 'день', 'дня', 'дней')}
          </div>
        )}
        {r.has_notes && <div className="hint">есть заметка — читать на брифинге</div>}
      </td>
      <td className="num" data-label="Чек-лист">
        {доля === null
          ? <span className="hint">пунктов нет</span>
          : <>{r.checklist_done ?? 0} / {r.checklist_total ?? 0}</>}
      </td>
      <td className="num" data-label="Вехи">
        {(r.milestones_total ?? 0) > 0
          ? <>{r.milestones_done ?? 0} / {r.milestones_total ?? 0}</>
          : <span className="hint">нет</span>}
      </td>
      <td data-label="Последнее движение">
        {r.last_event_at
          ? <>
              <div>{fmtDate(r.last_event_at, true)}</div>
              <div className="hint">{поКоду(refs?.event_type_names, r.last_event_type)}</div>
            </>
          : <span className="hint">записей нет</span>}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ журнал ═══════════════════════════════

function Журнал({ refs }: { refs?: СправочникиПроектов }) {
  const [тип, setТип] = useState('')
  const [сделка, setСделка] = useState('')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    type: тип,
    deal: сделка,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [тип, сделка, start])

  const запрос = useЖурналПроектов(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="coord-projects-log">
          <label htmlFor="coord-projects-log-type">
            Что произошло
            <select id="coord-projects-log-type" value={тип}
                    onChange={(e) => сСброса(setТип)(e.target.value)}>
              <option value="">всё</option>
              {(refs?.event_types ?? []).filter((т) => т.ev_type).map((т) => (
                <option key={String(т.ev_type)} value={String(т.ev_type)}>
                  {поКоду(refs?.event_type_names, т.ev_type)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="coord-projects-log-deal">
            Сделка
            <input id="coord-projects-log-deal" type="search" value={сделка}
                   placeholder="номер сделки"
                   onChange={(e) => сСброса(setСделка)(e.target.value)} />
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги
            ? ` · ${pluralN(итоги.hidden ?? 0, 'запись', 'записи', 'записей')} без текста`
              + ` · ${pluralN(итоги.auto ?? 0, 'запись', 'записи', 'записей')} автоматических`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаЖурнала строки={строки} refs={refs} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаЖурнала({ строки, refs }: {
  строки: СобытиеСтрока[]; refs?: СправочникиПроектов
}) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Записей журнала под этот отбор нет</div>
        <div className="empty-hint">
          Журнал пишет брифинг, когда координатор что-то меняет в карточке. Снимите
          фильтр по типу записи или по номеру сделки.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Когда</th>
            <th>Сделка</th>
            <th>Что произошло</th>
            <th>Подробности</th>
            <th>Кто</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={`${r.deal_bitrix_id}-${r.ev_id}`}>
              <td data-label="Когда">
                {r.ts ? fmtDate(r.ts, true) : <span className="hint">неизвестно</span>}
              </td>
              <td data-label="Сделка">
                <EntityLink kind="deal" id={r.deal_bitrix_id} title={r.deal_title} />
              </td>
              <td data-label="Что произошло">
                {поКоду(refs?.event_type_names, r.ev_type)}
              </td>
              <td data-label="Подробности">
                {r.text_hidden
                  ? (
                    <span className="hint">
                      текст не перенесён: в нём деньги, часы, километры или фамилии
                      бригады. Смотреть на брифинге
                    </span>
                  )
                  : (r.ev_text ?? <span className="hint">пусто</span>)}
              </td>
              <td data-label="Кто">
                {r.by_login ?? <span className="hint">неизвестно</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
